import React, { useState, useEffect, useRef } from 'react';
import { UserAddOutlined, DeleteOutlined, CloseOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Typography, Modal , message, Tooltip } from 'antd';

import StrikeTeamDetailsModel from '../../models/StrikeTeamDetailsModel';
import TrackerService from '../../utils/TrackerService';

const { Text } = Typography;
const { confirm } = Modal;

export const TeamMemberDeleteButton = ({ member, isAdmin, teamName, iconOnly = false, ...rest }) => {
    const { memberClientId, teamId } = member;
    const [displayMessage, setDisplayMessage] = useState(null);
    const [isMemberDeleted, setIsMemberDeleted] = useState(!!member.isDeleted);
    const isMountedRef = useRef(true);
    
    useEffect(() => {
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const updatedBlockedStateTo = (isBlocked) => {
        if (isMountedRef.current) {
            setIsMemberDeleted(isBlocked);
            setDisplayMessage(null);
        }
    };

    const deleteMember = async () => {
        const model = new StrikeTeamDetailsModel({
            teamId,
            memberClientId,
        });
        confirm({
            icon: <DeleteOutlined />,
            title: 'Deleting team member',
            content: 
            <>
              <br />
              <p>Please confirm you want to delete {member.displayName} from {teamName} team.</p> 
              <p>To restore a team member, re-send the invite link.</p>
            </>,
            okText: 'Delete',
            centered: true,
    
            onOk() {
              model.deleteMemberFromTeam(memberClientId);
              updatedBlockedStateTo(true);
            },
        });
    };

    const restoreMember = async () => {
        const model = new StrikeTeamDetailsModel({
            teamId,
            memberClientId,
        });
        if (isMountedRef.current) {
            setDisplayMessage('Restoring...');
        }
        message.info(`Restoring ${member.displayName}...`);
        await model.restoreMemberFromTeam(memberClientId);
        
        if (!isMountedRef.current) return; // Component unmounted, don't update state
        
        message.destroy();
        message.info(`${member.displayName} is restoring!`);
        if (isMountedRef.current) {
            setDisplayMessage('Restored');
            updatedBlockedStateTo(false);
        }
    };

    const isLocalHost = window.location.hostname === 'localhost';
    const isTest = isLocalHost;

    const tracker = new TrackerService({
      isTest,
    });

    const data = {
      memberClientId,
      memberDisplayName: member.displayName,
      teamId,
      teamName: member.teamName,
    }

    const onClickDelete = async () => {
      deleteMember();

      tracker.mixpanelTrack('delete_team_member', data);

      if (!isTest) {
        tracker.googleTrack('delete_team_member', data);
      }
    };

    const onClickRestore = async () => {
      restoreMember();

      tracker.mixpanelTrack('restore_team_member', data);

      if (!isTest) {
        tracker.googleTrack('restore_team_member', data);
      }
    };

    if (iconOnly) {
        // Remove iconOnly from rest props to avoid passing it to DOM
        const { iconOnly: _, ...buttonProps } = rest;
        return (
            <>
                {!isMemberDeleted &&
                    <Tooltip title="Delete member">
                        <Button 
                            key={`member-delete-button-${memberClientId}`} 
                            type='text' 
                            onClick={onClickDelete} 
                            {...buttonProps} 
                            danger 
                            icon={<CloseOutlined />}
                            shape="circle"
                            size="small"
                        />
                    </Tooltip>}
                {displayMessage === null && isMemberDeleted && isAdmin &&
                    <Tooltip title="Restore member">
                        <Button 
                            key={`member-restore-button-${memberClientId}`} 
                            type='text' 
                            onClick={onClickRestore} 
                            {...buttonProps} 
                            icon={<PlusOutlined />}
                            shape="circle"
                            size="small"
                        />
                    </Tooltip>}
                {displayMessage &&
                    <Text key={`member-delete-text-${memberClientId}`} {...buttonProps}>{displayMessage}</Text>}
            </> 
        );
    }

    return (
        <>
            {!isMemberDeleted &&
                <Button key={`member-delete-button-${memberClientId}`} type='link' onClick={onClickDelete} {...rest} danger icon={<DeleteOutlined />}>Delete</Button>}
            {displayMessage === null && isMemberDeleted && isAdmin &&
                <Button key={`member-restore-button-${memberClientId}`} type='link' onClick={onClickRestore} {...rest} icon={<UserAddOutlined />}>Restore</Button>}
            {displayMessage &&
                <Text key={`member-delete-text-${memberClientId}`} {...rest}>{displayMessage}</Text>}
        </> 
    );
};
